import { useFieldArray, useFormContext } from 'react-hook-form'
import { Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

// LotItemsField — shared multi-lot picker dùng cho cả PNK + PXK của 3 kho.
//
// mode='in':
//   - mỗi row có toggle "Lô mới" / "Lô có sẵn"
//   - lô mới: nhập code optional + select grade/material + qty
//   - lô cũ: select từ dropdown + qty
// mode='out':
//   - chỉ pick lô có sẵn (quantity_available > 0)

export interface LotOption {
    value: string // lot.id
    label: string // text cho trigger + filter (fallback khi không có render)
    available?: number // qty available cho mode='out' validation hint
    // Đơn vị hiển thị cho hint "Còn X <unit>". Mặc định 'kg' để giữ tương
    // thích với Kho phân loại / Kho lấy hương (luôn là kg); Kho bao bì
    // truyền unit thật của vật tư (cái, hộp, m...) để không hiện sai.
    unit?: string
    // render tuỳ chọn cho dropdown row (Combobox 2–3 dòng); trigger vẫn dùng `label`.
    render?: () => ReactNode
}

export interface CategoryOption {
    value: string // grade.id / material.id
    label: string
}

interface Props {
    mode: 'in' | 'out'
    lotOptions: LotOption[] // existing lots
    categoryOptions: CategoryOption[] // grades (sorting/scenting) hoặc materials (packaging)
    categoryLabel: string // "Phân loại" hoặc "Vật tư"
    lotPickerPlaceholder?: string
    fieldName?: string // mặc định 'items'
    // Render thêm UI per row (vd: PurchaseSourcesPicker cho sorting from_purchase).
    renderExtraPerItem?: (idx: number) => ReactNode
    // Khi true: Input KL của row hiển thị readonly + hint "= tổng nguồn"
    // (sorting from_purchase). Mặc định false.
    quantityReadonly?: boolean
    // Số row tối đa (vd: PXK for_scenting chỉ cho 1 lô). Default unlimited.
    maxItems?: number
    // Tên field lưu lot id khi kind='existing'. Default 'sorting_lot_id' để
    // tương thích Kho phân loại. Form Kho lấy hương / Kho bao bì truyền
    // 'scenting_lot_id' / 'packaging_lot_id' khớp schema Zod, nếu không sẽ
    // báo "Có lỗi ở các dòng item".
    existingIdField?: string
    // Tên field lưu category id khi kind='new'. Default 'new_lot_grade_id'.
    // Kho bao bì truyền 'new_lot_material_id'.
    newCategoryIdField?: string
    // Extra buttons render bên trái nút "Thêm lô" trên header (vd: nút Quét mã lô).
    headerAction?: ReactNode
    // Server-side search callback cho lot picker. Khi truyền, Combobox
    // disable client-side filter (trust server) — caller phải debounce trước
    // khi query API.
    onLotSearchChange?: (search: string) => void
    // Extra lot options inject vào Combobox — dùng cho SP quét mã vạch mà
    // lot nằm ngoài top N của useXLots. Parent fetch lot by ID sau scan rồi
    // truyền vào đây. Merge với lotOptions (scannedLots wins nếu trùng id).
    scannedLots?: LotOption[]
    // Server-side search cho category picker (vd: đơn mua ở kho lô chè mua —
    // danh sách dài, không thể tải hết về client). Khi truyền, Combobox tắt
    // client-side filter; caller phải debounce.
    onCategorySearchChange?: (search: string) => void
    // Khi false: ẩn toggle "Lô mới", mọi row bị khoá kind='existing'.
    // Dùng cho PNK hoàn từ phân loại — kg phải trả về lô đã tồn tại.
    allowNewLot?: boolean
}

interface RowExisting {
    kind: 'existing'
    sorting_lot_id: string
    quantity: number
}
interface RowNew {
    kind: 'new'
    new_lot_code?: string
    new_lot_grade_id: string // dùng chung cho material_id (form schema dùng tên này)
    quantity: number
}
export type LotItemRow = RowExisting | RowNew

export function LotItemsField({
    mode,
    lotOptions,
    categoryOptions,
    categoryLabel,
    lotPickerPlaceholder,
    fieldName = 'items',
    renderExtraPerItem,
    quantityReadonly = false,
    maxItems,
    existingIdField = 'sorting_lot_id',
    newCategoryIdField = 'new_lot_grade_id',
    headerAction,
    onLotSearchChange,
    scannedLots,
    onCategorySearchChange,
    allowNewLot = true,
}: Props) {
    const { control, register, watch, setValue } = useFormContext()
    // Cache category (grade/vật tư) + lô đã CHỌN TAY. `categoryOptions`/`lotOptions`
    // là search server-side dùng chung cho mọi row → chỉ chứa kết quả search hiện
    // tại. Không cache thì lựa chọn ở row A biến mất khỏi trigger ngay khi search
    // đổi (gõ tìm ở row B / mở lại picker rồi xoá ô tìm). Giá trị trong form vẫn
    // đúng nhưng trigger show placeholder → user tưởng mất.
    const [pickedCategories, setPickedCategories] = useState<
        Map<string, CategoryOption>
    >(new Map())
    const [pickedLots, setPickedLots] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const cacheCategory = useCallback((opt: CategoryOption) => {
        setPickedCategories((prev) => {
            if (prev.has(opt.value)) return prev
            const next = new Map(prev)
            next.set(opt.value, opt)
            return next
        })
    }, [])
    const cacheLot = useCallback((opt: LotOption) => {
        setPickedLots((prev) => {
            if (prev.has(opt.value)) return prev
            const next = new Map(prev)
            next.set(opt.value, opt)
            return next
        })
    }, [])
    // categoryOptions merge với cache lựa chọn tay.
    const mergedCategoryOptions = useMemo<CategoryOption[]>(() => {
        if (pickedCategories.size === 0) return categoryOptions
        const map = new Map<string, CategoryOption>()
        for (const opt of categoryOptions) map.set(opt.value, opt)
        for (const opt of pickedCategories.values()) map.set(opt.value, opt)
        return Array.from(map.values())
    }, [categoryOptions, pickedCategories])
    // Merge lotOptions từ server + scannedLots (quét mã) + pickedLots (chọn tay).
    const mergedLotOptions = useMemo<LotOption[]>(() => {
        if (
            (!scannedLots || scannedLots.length === 0) &&
            pickedLots.size === 0
        ) {
            return lotOptions
        }
        const map = new Map<string, LotOption>()
        for (const opt of lotOptions) map.set(opt.value, opt)
        for (const opt of pickedLots.values()) map.set(opt.value, opt)
        for (const opt of scannedLots ?? []) map.set(opt.value, opt)
        return Array.from(map.values())
    }, [lotOptions, scannedLots, pickedLots])
    const { fields, append, remove } = useFieldArray({
        control,
        name: fieldName,
    })
    // Đọc toàn bộ items để loại lô đã chọn ở row khác khỏi dropdown row hiện
    // tại — tránh user chọn trùng 1 lô 2 lần trong cùng phiếu.
    const allItems = watch(fieldName) as
        | Array<Record<string, unknown>>
        | undefined
    const selectedLotIdsByIndex = useMemo(() => {
        return (allItems ?? []).map(
            (row) => (row?.[existingIdField] as string | undefined) ?? '',
        )
    }, [allItems, existingIdField])

    // Khi allowNewLot tắt (vd đổi lý do sang "hoàn"), ép mọi row đang là 'new'
    // về 'existing'. Nếu không, row cũ kẹt ở 'new' TRONG KHI toggle đã bị ẩn —
    // user không còn đường đổi, submit thì BE từ chối → kẹt cứng.
    useEffect(() => {
        if (allowNewLot) return
        ;(allItems ?? []).forEach((row, idx) => {
            if (row?.kind !== 'new') return
            setValue(`${fieldName}.${idx}.kind`, 'existing')
            setValue(`${fieldName}.${idx}.${existingIdField}`, '')
            setValue(`${fieldName}.${idx}.${newCategoryIdField}`, undefined)
            setValue(`${fieldName}.${idx}.new_lot_code`, undefined)
        })
    }, [
        allowNewLot,
        allItems,
        fieldName,
        existingIdField,
        newCategoryIdField,
        setValue,
    ])

    const addRow = () => {
        if (mode === 'in' && allowNewLot) {
            append({ kind: 'new', [newCategoryIdField]: '', quantity: 0 })
        } else {
            append({ kind: 'existing', [existingIdField]: '', quantity: 0 })
        }
    }

    // Thanh nút "Thêm lô" + "Quét mã" đặt DƯỚI danh sách item: user thêm rồi sửa
    // ngay tại chỗ, không phải kéo lên đầu form mỗi lần muốn thêm dòng.
    const actionBar = (
        <div className="flex items-center gap-2">
            {headerAction}
            {(maxItems === undefined || fields.length < maxItems) && (
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addRow}
                >
                    <Plus className="size-4 mr-1" /> {m.lot_items_add()}
                </Button>
            )}
        </div>
    )

    return (
        <div className="space-y-3">
            <Label>{m.lot_items_title()}</Label>

            {fields.length === 0 && (
                <div className="rounded-md border border-dashed bg-muted/30 py-6 text-center text-sm text-muted-foreground">
                    {m.lot_items_empty()}
                </div>
            )}

            {fields.map((field, idx) => {
                const kind = watch(`${fieldName}.${idx}.kind`) as
                    | 'new'
                    | 'existing'
                return (
                    <div
                        key={field.id}
                        className="grid gap-3 rounded-md border bg-card p-3 min-w-0"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-muted-foreground">
                                {m.lot_items_row({ n: idx + 1 })}
                            </span>
                            <div className="flex items-center gap-2">
                                {mode === 'in' && allowNewLot && (
                                    <div className="flex rounded-md border p-0.5 text-xs">
                                        <button
                                            type="button"
                                            className={`px-2 py-1 rounded ${kind === 'new' ? 'bg-primary text-primary-foreground' : ''}`}
                                            onClick={() => {
                                                setValue(
                                                    `${fieldName}.${idx}.kind`,
                                                    'new',
                                                )
                                                setValue(
                                                    `${fieldName}.${idx}.${existingIdField}`,
                                                    '',
                                                )
                                            }}
                                        >
                                            {m.lot_items_new_lot()}
                                        </button>
                                        <button
                                            type="button"
                                            className={`px-2 py-1 rounded ${kind === 'existing' ? 'bg-primary text-primary-foreground' : ''}`}
                                            onClick={() => {
                                                setValue(
                                                    `${fieldName}.${idx}.kind`,
                                                    'existing',
                                                )
                                                setValue(
                                                    `${fieldName}.${idx}.${newCategoryIdField}`,
                                                    '',
                                                )
                                                setValue(
                                                    `${fieldName}.${idx}.new_lot_code`,
                                                    '',
                                                )
                                            }}
                                        >
                                            {m.lot_items_existing_lot()}
                                        </button>
                                    </div>
                                )}
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => remove(idx)}
                                >
                                    <Trash2 className="size-4 text-destructive" />
                                </Button>
                            </div>
                        </div>

                        {/* Lô mới — chỉ mode='in' */}
                        {mode === 'in' && kind === 'new' && (
                            <div className="grid gap-3 md:grid-cols-3">
                                <div>
                                    <Label className="text-xs">
                                        {m.lot_items_new_lot_code()}
                                    </Label>
                                    <Input
                                        {...register(
                                            `${fieldName}.${idx}.new_lot_code`,
                                        )}
                                        placeholder={m.lot_items_new_lot_code_placeholder()}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.new_lot_code`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {categoryLabel}
                                    </Label>
                                    <Combobox
                                        options={mergedCategoryOptions}
                                        value={watch(
                                            `${fieldName}.${idx}.${newCategoryIdField}`,
                                        )}
                                        onChange={(v) => {
                                            setValue(
                                                `${fieldName}.${idx}.${newCategoryIdField}`,
                                                v,
                                            )
                                            const opt =
                                                mergedCategoryOptions.find(
                                                    (o) => o.value === v,
                                                )
                                            if (opt) cacheCategory(opt)
                                        }}
                                        onSearchChange={onCategorySearchChange}
                                        shouldFilter={!onCategorySearchChange}
                                        onOpenChange={
                                            onCategorySearchChange
                                                ? (open) => {
                                                      if (!open)
                                                          onCategorySearchChange(
                                                              '',
                                                          )
                                                  }
                                                : undefined
                                        }
                                        placeholder={m.lot_items_choose_category(
                                            {
                                                category:
                                                    categoryLabel.toLowerCase(),
                                            },
                                        )}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.${newCategoryIdField}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.lot_items_quantity()}
                                    </Label>
                                    <Input
                                        type="number"
                                        step="0.001"
                                        min={0}
                                        readOnly={quantityReadonly}
                                        tabIndex={quantityReadonly ? -1 : 0}
                                        className={
                                            quantityReadonly
                                                ? 'bg-muted cursor-not-allowed'
                                                : undefined
                                        }
                                        {...register(
                                            `${fieldName}.${idx}.quantity`,
                                            { valueAsNumber: true },
                                        )}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.quantity`}
                                    />
                                    {quantityReadonly && (
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            {m.lot_items_quantity_auto_hint()}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Lô có sẵn — cho cả mode='in' (toggle) và mode='out' */}
                        {(mode === 'out' ||
                            (mode === 'in' && kind === 'existing')) && (
                            <div className="grid gap-3 md:grid-cols-2">
                                <div className="min-w-0">
                                    <Label className="text-xs">
                                        {m.lot_items_select_lot()}
                                    </Label>
                                    <Combobox
                                        options={mergedLotOptions.filter(
                                            (o) => {
                                                const current =
                                                    selectedLotIdsByIndex[idx]
                                                if (o.value === current)
                                                    return true
                                                return !selectedLotIdsByIndex.some(
                                                    (id, i) =>
                                                        i !== idx &&
                                                        id === o.value,
                                                )
                                            },
                                        )}
                                        value={watch(
                                            `${fieldName}.${idx}.${existingIdField}`,
                                        )}
                                        onChange={(v) => {
                                            setValue(
                                                `${fieldName}.${idx}.${existingIdField}`,
                                                v,
                                            )
                                            const opt = mergedLotOptions.find(
                                                (o) => o.value === v,
                                            )
                                            if (opt) cacheLot(opt)
                                        }}
                                        onSearchChange={onLotSearchChange}
                                        shouldFilter={
                                            onLotSearchChange ? false : true
                                        }
                                        onOpenChange={
                                            onLotSearchChange
                                                ? (open) => {
                                                      if (!open)
                                                          onLotSearchChange('')
                                                  }
                                                : undefined
                                        }
                                        placeholder={
                                            lotPickerPlaceholder ??
                                            m.lot_items_lot_picker_placeholder()
                                        }
                                        searchPlaceholder={m.lot_items_lot_picker_search()}
                                    />
                                    <FieldError
                                        name={`${fieldName}.${idx}.${existingIdField}`}
                                    />
                                    {mode === 'out' && (
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            {(() => {
                                                const lotId = watch(
                                                    `${fieldName}.${idx}.${existingIdField}`,
                                                )
                                                const lot =
                                                    mergedLotOptions.find(
                                                        (l) =>
                                                            l.value === lotId,
                                                    )
                                                if (
                                                    lot &&
                                                    typeof lot.available ===
                                                        'number'
                                                ) {
                                                    return m.lot_items_available_hint(
                                                        {
                                                            qty: formatQty(
                                                                lot.available,
                                                            ),
                                                            unit:
                                                                lot.unit ??
                                                                'kg',
                                                        },
                                                    )
                                                }
                                                return null
                                            })()}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <Label className="text-xs">
                                        {m.lot_items_quantity()}
                                    </Label>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            type="number"
                                            step="0.001"
                                            min={0}
                                            readOnly={quantityReadonly}
                                            tabIndex={quantityReadonly ? -1 : 0}
                                            className={
                                                quantityReadonly
                                                    ? 'bg-muted cursor-not-allowed'
                                                    : undefined
                                            }
                                            {...register(
                                                `${fieldName}.${idx}.quantity`,
                                                { valueAsNumber: true },
                                            )}
                                        />
                                        {mode === 'out' &&
                                            !quantityReadonly &&
                                            (() => {
                                                const lotId = watch(
                                                    `${fieldName}.${idx}.${existingIdField}`,
                                                )
                                                const lot =
                                                    mergedLotOptions.find(
                                                        (l) =>
                                                            l.value === lotId,
                                                    )
                                                const avail = lot?.available
                                                return (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        disabled={
                                                            avail ===
                                                                undefined ||
                                                            avail <= 0
                                                        }
                                                        onClick={() => {
                                                            if (
                                                                avail !==
                                                                undefined
                                                            ) {
                                                                setValue(
                                                                    `${fieldName}.${idx}.quantity`,
                                                                    avail,
                                                                    {
                                                                        shouldValidate: true,
                                                                        shouldDirty: true,
                                                                    },
                                                                )
                                                            }
                                                        }}
                                                    >
                                                        {m.lot_items_take_all()}
                                                    </Button>
                                                )
                                            })()}
                                    </div>
                                    <FieldError
                                        name={`${fieldName}.${idx}.quantity`}
                                    />
                                </div>
                            </div>
                        )}

                        {renderExtraPerItem?.(idx)}
                    </div>
                )
            })}

            {actionBar}
        </div>
    )
}
